import React, { useEffect, useRef, useState } from "react";
import { Package } from "lucide-react";

const GAME_WIDTH = 280;
const GAME_HEIGHT = 200;
const BASKET_WIDTH = 56;
const BASKET_Y = GAME_HEIGHT - 26;
const FALL_MS = 2200; // time for a box to fall top -> bottom
const SPAWN_MS = 700;

// A tiny, fully self-contained "catch the falling boxes" game — shown
// while a report loads. Purely for fun during an otherwise-idle wait;
// has zero effect on the actual loading logic. Controlled with
// ArrowLeft/ArrowRight or by moving the mouse over the game area.
const LoadingGame = ({ label = "Loading..." }) => {
    const [basketX, setBasketX] = useState(GAME_WIDTH / 2 - BASKET_WIDTH / 2);
    const [items, setItems] = useState([]); // { id, x, spawnedAt, caught, missed }
    const [score, setScore] = useState(0);
    const [best, setBest] = useState(() => {
        try { return Number(localStorage.getItem("msp_loading_game_best") || 0); } catch { return 0; }
    });
    const containerRef = useRef(null);
    const basketXRef = useRef(basketX);
    basketXRef.current = basketX;
    const nextIdRef = useRef(0);

    // Keyboard controls
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === "ArrowLeft") setBasketX((x) => Math.max(0, x - 22));
            if (e.key === "ArrowRight") setBasketX((x) => Math.min(GAME_WIDTH - BASKET_WIDTH, x + 22));
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, []);

    // Mouse control — move directly to cursor position over the game area
    const handleMouseMove = (e) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const relX = e.clientX - rect.left - BASKET_WIDTH / 2;
        setBasketX(Math.max(0, Math.min(GAME_WIDTH - BASKET_WIDTH, relX)));
    };

    // Spawn loop
    useEffect(() => {
        const spawn = setInterval(() => {
            setItems((prev) => [
                ...prev,
                { id: nextIdRef.current++, x: Math.random() * (GAME_WIDTH - 24), spawnedAt: Date.now() },
            ]);
        }, SPAWN_MS);
        return () => clearInterval(spawn);
    }, []);

    // Animation + collision loop
    useEffect(() => {
        let frame;
        const tick = () => {
            const now = Date.now();
            setItems((prev) =>
                prev
                    .map((it) => {
                        if (it.caught || it.missed) return it;
                        const progress = (now - it.spawnedAt) / FALL_MS;
                        const y = progress * (BASKET_Y - 20);
                        if (progress >= 1) {
                            return { ...it, missed: true };
                        }
                        if (y >= BASKET_Y - 24) {
                            const withinBasket = it.x + 12 >= basketXRef.current && it.x + 12 <= basketXRef.current + BASKET_WIDTH;
                            if (withinBasket) {
                                setScore((s) => {
                                    const next = s + 1;
                                    setBest((b) => {
                                        if (next > b) {
                                            try { localStorage.setItem("msp_loading_game_best", String(next)); } catch { /* ignore */ }
                                            return next;
                                        }
                                        return b;
                                    });
                                    return next;
                                });
                                return { ...it, caught: true };
                            }
                        }
                        return it;
                    })
                    .filter((it) => now - it.spawnedAt < FALL_MS + 300) // clean up finished items
            );
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, []);

    return (
        <div className="flex flex-col items-center py-8 select-none">
            <p className="text-gray-400 text-sm mb-3">{label}</p>
            <div
                ref={containerRef}
                onMouseMove={handleMouseMove}
                className="relative bg-gradient-to-b from-teal-50 to-blue-50 rounded-2xl border border-gray-100 overflow-hidden cursor-none"
                style={{ width: GAME_WIDTH, height: GAME_HEIGHT }}
            >
                {items.filter((it) => !it.caught).map((it) => {
                    const progress = Math.min(1, (Date.now() - it.spawnedAt) / FALL_MS);
                    const y = progress * (BASKET_Y - 20);
                    return (
                        <div key={it.id} className="absolute transition-opacity"
                            style={{ left: it.x, top: y, opacity: it.missed ? 0 : 1 }}>
                            <img src="/logo512.png" style={{ height: 24, width: 24 }} />
                        </div>
                    );
                })}
                <div className="absolute bg-teal-600 rounded-t-lg shadow"
                    style={{ left: basketX, top: BASKET_Y, width: BASKET_WIDTH, height: 16 }} />
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                <span>Score: <span className="font-bold text-gray-600">{score}</span></span>
                <span>Best: <span className="font-bold text-gray-600">{best}</span></span>
                <span className="text-gray-300">← → or move mouse to catch boxes</span>
            </div>
        </div>
    );
};

export default LoadingGame;